import React, { useEffect, useRef, useState } from 'react';
import { Plus, Save, Edit, Building2, Users, Search, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import apiService from '../services/apiService';
import Modal from './ui/Modal';
import { useConfirm } from './ui/ConfirmDialog';
import { useI18n } from '../utils/i18n';
import { useToast } from './ToastProvider';
import { invalidateCustomerCache } from '../utils/customerCache';
import { invalidateContactCache } from '../utils/contactCache';
import { kundePasstZurSuche } from '../utils/suchFilter';
import { invalidateKontaktVerzeichnis } from '../utils/kontaktVerzeichnis';

// #87: Kundenverwaltung als eigenständige Komponente — aus dem AdminPanel
// extrahiert, damit Manager sie ohne Admin-Bereich nutzen können (Seite
// /customers über das Optionen-Menü; Server-Routen: requireManagerOrAdmin).
// #105: inkl. Ansprechpartner-Pflege je Kunde (Modal).
// #204: Archivieren/Reaktivieren + Löschen direkt in der Zeile (hartes Löschen
// nur ohne Referenzen — Server antwortet sonst 409 mit Zählern) und
// Dubletten-Warnung beim Anlegen (Name/Telefon warnt, Domäne blockt der Server).
// #205: clientseitige Suche über Name/Kurzform/Domänen/Telefon/Ansprechpartner.
// #209: „Bearbeiten" scrollt zum Formular und fokussiert das Namensfeld.
const emptyCustomerForm = { name: '', shortName: '', domains: '', phone1: '', phone2: '', phone3: '', notes: '', isActive: true };
const emptyContactForm = { name: '', phoneMobile: '', phoneLandline: '', email: '', notes: '', isActive: true };

// #204 D: Normalisierung fürs Dubletten-Warnen (bewusst grob — warnt nur)
const normName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9äöüß]/g, '');
const normPhone = (s) => String(s || '').replace(/[^\d]/g, '');

const CustomerAdmin = () => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [error, setError] = useState(null);
  // #205: Suchfeld (clientseitig)
  const [search, setSearch] = useState('');
  // #204: Zeilen-Aktion läuft (Archivieren/Löschen) — sperrt die Buttons
  const [rowBusyId, setRowBusyId] = useState(null);
  // #209: Scroll-Ziele der beiden Formulare
  const customerFormRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const contactFormRef = useRef(null);
  const contactNameInputRef = useRef(null);
  // #105: Ansprechpartner-Modal
  const [contactsCustomer, setContactsCustomer] = useState(null);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [editingContactId, setEditingContactId] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [contactRowBusyId, setContactRowBusyId] = useState(null);

  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      const resp = await apiService.getCustomersAdmin();
      setCustomers(resp.customers || []);
      return resp.customers || [];
    } catch (e) {
      console.error('Error loading customers:', e);
      setError(t('admin.customers.loadError', 'Fehler beim Laden der Kunden.'));
      return null;
    } finally {
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEditCustomer = (c) => {
    setEditingCustomerId(c.id);
    setCustomerForm({
      name: c.name || '',
      shortName: c.shortName || '',
      domains: (c.domains || []).join(', '),
      phone1: c.phone1 || '',
      phone2: c.phone2 || '',
      phone3: c.phone3 || '',
      notes: c.notes || '',
      isActive: c.isActive !== false
    });
    // #209: Formular steht über der Tabelle — hinscrollen und Fokus setzen.
    // scrollIntoView statt window.scrollTo: im Admin-Panel scrollt ein
    // Container, nicht das Fenster. setTimeout: erst nach dem Re-Render.
    setTimeout(() => {
      try {
        customerFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        customerNameInputRef.current?.focus({ preventScroll: true });
      } catch (_) {}
    }, 0);
  };

  const resetCustomerForm = () => {
    setEditingCustomerId(null);
    setCustomerForm(emptyCustomerForm);
  };

  // ----- #105: Ansprechpartner -----
  const openContacts = (c) => {
    setContactsCustomer(c);
    setEditingContactId(null);
    setContactForm(emptyContactForm);
    setContactError(null);
  };

  const startEditContact = (k) => {
    setEditingContactId(k.id);
    setContactForm({
      name: k.name || '',
      phoneMobile: k.phoneMobile || '',
      phoneLandline: k.phoneLandline || '',
      email: k.email || '',
      notes: k.notes || '',
      isActive: k.isActive !== false
    });
    // #209: wie beim Kunden — das Formular sitzt oben im Modal
    setTimeout(() => {
      try {
        contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        contactNameInputRef.current?.focus({ preventScroll: true });
      } catch (_) {}
    }, 0);
  };

  const resetContactForm = () => {
    setEditingContactId(null);
    setContactForm(emptyContactForm);
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactsCustomer) return;
    setSavingContact(true);
    setContactError(null);
    try {
      const payload = {
        name: contactForm.name,
        phoneMobile: contactForm.phoneMobile,
        phoneLandline: contactForm.phoneLandline,
        email: contactForm.email,
        notes: contactForm.notes,
        isActive: contactForm.isActive
      };
      if (editingContactId) {
        await apiService.updateCustomerContact(editingContactId, payload);
      } else {
        await apiService.createCustomerContact(contactsCustomer.id, payload);
      }
      invalidateContactCache(contactsCustomer.id);
      invalidateKontaktVerzeichnis(); // #255
      pushToast({ message: t('admin.contacts.saved', 'Ansprechpartner gespeichert.'), type: 'success' });
      resetContactForm();
      const fresh = await loadCustomers();
      if (fresh) {
        const updated = fresh.find(x => x.id === contactsCustomer.id);
        if (updated) setContactsCustomer(updated);
      }
    } catch (err) {
      console.error('Error saving customer contact:', err);
      setContactError(err.response?.data?.error || t('admin.contacts.saveError', 'Fehler beim Speichern des Ansprechpartners.'));
    } finally {
      setSavingContact(false);
    }
  };

  // #204 D: mögliche Dubletten VOR dem Anlegen finden — gleicher/ähnlicher
  // Name oder gleiche Telefonnummer warnt (Anlage bleibt möglich); die
  // Domänen-Kollision blockt der Server ohnehin hart (409, saveCustomerDomains).
  const findSimilarCustomers = () => {
    const nameNorm = normName(customerForm.name);
    const phones = [customerForm.phone1, customerForm.phone2, customerForm.phone3]
      .map(normPhone).filter(p => p.length >= 4);
    return customers.filter(c => {
      const cn = normName(c.name);
      const cs = normName(c.shortName);
      const nameHit = !!nameNorm && (
        cn === nameNorm || cs === nameNorm ||
        (nameNorm.length >= 4 && (cn.includes(nameNorm) || nameNorm.includes(cn)))
      );
      const phoneHit = phones.length > 0 &&
        [c.phone1, c.phone2, c.phone3].map(normPhone).some(p => p && phones.includes(p));
      return nameHit || phoneHit;
    });
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (!customerForm.name.trim()) return;
    if (!editingCustomerId) {
      const similar = findSimilarCustomers();
      if (similar.length > 0) {
        const names = similar.slice(0, 3).map(c => c.name).join(', ');
        const ok = await confirm({
          title: t('admin.customers.similarTitle', 'Mögliche Dublette'),
          message: t('admin.customers.similarMessage', 'Ähnliche Kunden gefunden: {{names}}. Trotzdem anlegen?', { names }),
          confirmLabel: t('admin.customers.similarConfirm', 'Trotzdem anlegen')
        });
        if (!ok) return;
      }
    }
    setSavingCustomer(true);
    setError(null);
    try {
      const payload = {
        name: customerForm.name,
        shortName: customerForm.shortName,
        phone1: customerForm.phone1,
        phone2: customerForm.phone2,
        phone3: customerForm.phone3,
        notes: customerForm.notes,
        isActive: customerForm.isActive,
        domains: customerForm.domains.split(',').map(d => d.trim()).filter(Boolean)
      };
      if (editingCustomerId) {
        await apiService.updateCustomer(editingCustomerId, payload);
      } else {
        await apiService.createCustomer(payload);
      }
      invalidateCustomerCache();
      invalidateKontaktVerzeichnis(); // #255
      pushToast({ message: t('admin.customers.saved', 'Kunde gespeichert.'), type: 'success' });
      resetCustomerForm();
      await loadCustomers();
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err.response?.data?.error || t('admin.customers.saveError', 'Fehler beim Speichern des Kunden.'));
    } finally {
      setSavingCustomer(false);
    }
  };

  // ----- #204: Zeilen-Aktionen (Archivieren/Reaktivieren + Löschen) -----

  const describeCustomerRefs = (refs) => [
    refs?.tickets ? `${refs.tickets} ${t('admin.customers.refsTickets', 'Tickets')}` : null,
    refs?.archivedTickets ? `${refs.archivedTickets} ${t('admin.customers.refsArchived', 'Archiv-Tickets')}` : null,
    refs?.projects ? `${refs.projects} ${t('admin.customers.refsProjects', 'Vorgänge')}` : null,
    refs?.contacts ? `${refs.contacts} ${t('admin.customers.refsContacts', 'Ansprechpartner')}` : null,
    refs?.domains ? `${refs.domains} ${t('admin.customers.refsDomains', 'Domänen')}` : null
  ].filter(Boolean).join(', ');

  const handleToggleCustomerActive = async (c) => {
    const activate = c.isActive === false;
    setRowBusyId(c.id);
    try {
      await apiService.updateCustomer(c.id, { isActive: activate });
      invalidateCustomerCache();
      invalidateKontaktVerzeichnis(); // #255
      pushToast({
        message: activate
          ? t('admin.customers.reactivated', 'Kunde reaktiviert.')
          : t('admin.customers.archived', 'Kunde archiviert.'),
        type: 'success'
      });
      await loadCustomers();
    } catch (err) {
      console.error('Toggle customer active error:', err);
      pushToast({ message: err.response?.data?.error || t('admin.customers.saveError', 'Fehler beim Speichern des Kunden.'), type: 'error' });
    } finally {
      setRowBusyId(null);
    }
  };

  const handleDeleteCustomer = async (c) => {
    const ok = await confirm({
      title: t('admin.customers.deleteTitle', 'Kunde löschen'),
      message: t('admin.customers.deleteMessage', 'Kunde „{{name}}" endgültig löschen? Das geht nur, solange keine Tickets, Vorgänge, Ansprechpartner oder Domänen darauf verweisen.', { name: c.name }),
      confirmLabel: t('common.delete', 'Löschen'),
      danger: true
    });
    if (!ok) return;
    setRowBusyId(c.id);
    try {
      await apiService.deleteCustomer(c.id);
      invalidateCustomerCache();
      invalidateKontaktVerzeichnis(); // #255
      pushToast({ message: t('admin.customers.deleted', 'Kunde gelöscht.'), type: 'success' });
      if (editingCustomerId === c.id) resetCustomerForm();
      await loadCustomers();
    } catch (err) {
      const refs = err.response?.data?.refs;
      if (err.response?.status === 409 && refs) {
        pushToast({
          message: t('admin.customers.deleteRefs', 'Kunde wird noch verwendet ({{refs}}) — bitte stattdessen archivieren.', { refs: describeCustomerRefs(refs) }),
          type: 'error'
        });
      } else {
        console.error('Delete customer error:', err);
        pushToast({ message: err.response?.data?.error || t('admin.customers.deleteError', 'Fehler beim Löschen des Kunden.'), type: 'error' });
      }
    } finally {
      setRowBusyId(null);
    }
  };

  const handleToggleContactActive = async (k) => {
    if (!contactsCustomer) return;
    const activate = k.isActive === false;
    setContactRowBusyId(k.id);
    try {
      await apiService.updateCustomerContact(k.id, { isActive: activate });
      invalidateContactCache(contactsCustomer.id);
      invalidateKontaktVerzeichnis(); // #255
      pushToast({
        message: activate
          ? t('admin.contacts.reactivated', 'Ansprechpartner reaktiviert.')
          : t('admin.contacts.archived', 'Ansprechpartner archiviert.'),
        type: 'success'
      });
      const fresh = await loadCustomers();
      if (fresh) {
        const updated = fresh.find(x => x.id === contactsCustomer.id);
        if (updated) setContactsCustomer(updated);
      }
    } catch (err) {
      console.error('Toggle contact active error:', err);
      pushToast({ message: err.response?.data?.error || t('admin.contacts.saveError', 'Fehler beim Speichern des Ansprechpartners.'), type: 'error' });
    } finally {
      setContactRowBusyId(null);
    }
  };

  const handleDeleteContact = async (k) => {
    if (!contactsCustomer) return;
    const ok = await confirm({
      title: t('admin.contacts.deleteTitle', 'Ansprechpartner löschen'),
      message: t('admin.contacts.deleteMessage', 'Ansprechpartner „{{name}}" endgültig löschen? Das geht nur, solange keine Tickets darauf verweisen.', { name: k.name }),
      confirmLabel: t('common.delete', 'Löschen'),
      danger: true
    });
    if (!ok) return;
    setContactRowBusyId(k.id);
    try {
      await apiService.deleteCustomerContact(k.id);
      invalidateContactCache(contactsCustomer.id);
      invalidateKontaktVerzeichnis(); // #255
      pushToast({ message: t('admin.contacts.deleted', 'Ansprechpartner gelöscht.'), type: 'success' });
      if (editingContactId === k.id) resetContactForm();
      const fresh = await loadCustomers();
      if (fresh) {
        const updated = fresh.find(x => x.id === contactsCustomer.id);
        if (updated) setContactsCustomer(updated);
      }
    } catch (err) {
      const refs = err.response?.data?.refs;
      if (err.response?.status === 409 && refs) {
        const parts = [
          refs.tickets ? `${refs.tickets} ${t('admin.customers.refsTickets', 'Tickets')}` : null,
          refs.archivedTickets ? `${refs.archivedTickets} ${t('admin.customers.refsArchived', 'Archiv-Tickets')}` : null
        ].filter(Boolean).join(', ');
        pushToast({
          message: t('admin.contacts.deleteRefs', 'Ansprechpartner wird noch verwendet ({{refs}}) — bitte stattdessen deaktivieren.', { refs: parts }),
          type: 'error'
        });
      } else {
        console.error('Delete contact error:', err);
        pushToast({ message: err.response?.data?.error || t('admin.contacts.deleteError', 'Fehler beim Löschen des Ansprechpartners.'), type: 'error' });
      }
    } finally {
      setContactRowBusyId(null);
    }
  };

  // ----- #205: clientseitige Suche (inkl. inaktiver Einträge — die müssen
  // auffindbar bleiben, „deaktivieren statt löschen" ist der Normalfall) -----
  // #251/#255: die Regel liegt jetzt in utils/suchFilter (dieselbe nutzt die
  // Kontaktsuche in der Kopfleiste); mehrere Wörter müssen alle passen.
  const visibleCustomers = !search.trim() ? customers : customers.filter(c => kundePasstZurSuche(c, search));

  return (
    <div className="admin-section">
      <h3>{t('admin.tabs.customers', 'Kunden')}</h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: -6 }}>
        {t('admin.customers.intro', 'Kundenstamm für die Zuordnung von Tickets. Eingehende Mails werden über die E-Mail-Domäne automatisch dem Kunden zugeordnet; die Kurzform wird in Tickets angezeigt.')}
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <form ref={customerFormRef} onSubmit={handleSaveCustomer} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, alignItems: 'end', marginBottom: 16, background: 'var(--color-bg)', padding: 12, borderRadius: 6 }}>
        <div className="form-group">
          <label>{t('admin.customers.name', 'Kundenname')} *</label>
          <input ref={customerNameInputRef} type="text" className="form-input" value={customerForm.name}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('admin.customers.namePlaceholder', 'z. B. Krebs Consulting & IT-Services Co KG')}
            disabled={savingCustomer} required />
        </div>
        <div className="form-group">
          <label>{t('admin.customers.shortName', 'Kurzform (Anzeige in Tickets)')}</label>
          <input type="text" className="form-input" value={customerForm.shortName}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, shortName: e.target.value }))}
            placeholder={t('admin.customers.shortNamePlaceholder', 'z. B. Krebs IT')}
            disabled={savingCustomer} />
        </div>
        <div className="form-group">
          <label>{t('admin.customers.domains', 'E-Mail-Domänen (Komma-getrennt)')}</label>
          <input type="text" className="form-input" value={customerForm.domains}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, domains: e.target.value }))}
            placeholder={t('admin.customers.domainsPlaceholder', 'krebsit.de, krebs-itservices.de')}
            disabled={savingCustomer} />
        </div>
        <div className="form-group">
          <label>{t('admin.customers.phone1', 'Telefon 1')}</label>
          <input type="text" className="form-input" value={customerForm.phone1}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, phone1: e.target.value }))} disabled={savingCustomer} />
        </div>
        <div className="form-group">
          <label>{t('admin.customers.phone2', 'Telefon 2')}</label>
          <input type="text" className="form-input" value={customerForm.phone2}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, phone2: e.target.value }))} disabled={savingCustomer} />
        </div>
        <div className="form-group">
          <label>{t('admin.customers.phone3', 'Telefon 3')}</label>
          <input type="text" className="form-input" value={customerForm.phone3}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, phone3: e.target.value }))} disabled={savingCustomer} />
        </div>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>{t('admin.customers.notes', 'Notizen')}</label>
          <textarea className="form-textarea" rows={2} value={customerForm.notes}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, notes: e.target.value }))} disabled={savingCustomer} />
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={customerForm.isActive}
            onChange={(e) => setCustomerForm(prev => ({ ...prev, isActive: e.target.checked }))} disabled={savingCustomer} />
          {t('admin.customers.active', 'Aktiv')}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn btn-primary" disabled={savingCustomer || !customerForm.name.trim()}>
            {editingCustomerId ? <Save size={16} /> : <Plus size={16} />}
            {editingCustomerId
              ? t('admin.customers.save', 'Speichern')
              : t('admin.customers.add', 'Kunde anlegen')}
          </button>
          {editingCustomerId && (
            <button type="button" className="btn btn-secondary" onClick={resetCustomerForm} disabled={savingCustomer}>
              {t('admin.customers.cancelEdit', 'Abbrechen')}
            </button>
          )}
        </div>
      </form>

      {/* #205: Suche — filtert clientseitig, inaktive Treffer bleiben sichtbar */}
      <div className="form-group" style={{ marginBottom: 12, maxWidth: 420 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
          <input
            type="search"
            className="form-input"
            style={{ paddingLeft: 34 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.customers.searchPlaceholder', 'Suchen: Name, Kurzform, Domäne, Telefon, Ansprechpartner…')}
            aria-label={t('admin.customers.searchLabel', 'Kunden durchsuchen')}
          />
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('admin.customers.name', 'Kundenname')}</th>
              <th>{t('admin.customers.shortNameCol', 'Kurzform')}</th>
              <th>{t('admin.customers.domainsCol', 'Domänen')}</th>
              <th>{t('admin.customers.phoneCol', 'Telefon')}</th>
              <th>{t('admin.contacts.countCol', 'Ansprechpartner')}</th>
              <th>{t('admin.customers.activeCol', 'Aktiv')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleCustomers.map(c => (
              <tr key={c.id} style={c.isActive === false ? { opacity: 0.55 } : undefined}>
                <td>{c.name}</td>
                <td>{c.shortName || '—'}</td>
                <td>{(c.domains || []).map(d => `@${d}`).join(', ') || '—'}</td>
                <td>{[c.phone1, c.phone2, c.phone3].filter(Boolean).join(' / ') || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => openContacts(c)}
                    title={t('admin.contacts.manage', 'Ansprechpartner verwalten')}
                  >
                    <Users size={14} />
                    {(c.contacts || []).filter(k => k.isActive !== false).length}
                  </button>
                </td>
                <td>{c.isActive !== false ? '✓' : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => startEditCustomer(c)} disabled={rowBusyId === c.id}>
                      <Edit size={14} />
                      {t('admin.customers.edit', 'Bearbeiten')}
                    </button>
                    {/* #204: Deaktivieren gab es nur als Häkchen im Dialog — jetzt sichtbar in der Zeile */}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleToggleCustomerActive(c)}
                      disabled={rowBusyId === c.id}
                      title={c.isActive !== false
                        ? t('admin.customers.archiveAction', 'Archivieren')
                        : t('admin.customers.reactivateAction', 'Reaktivieren')}
                    >
                      {c.isActive !== false ? <Archive size={14} /> : <ArchiveRestore size={14} />}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleDeleteCustomer(c)}
                      disabled={rowBusyId === c.id}
                      title={t('common.delete', 'Löschen')} aria-label={t('common.delete', 'Löschen')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {customers.length === 0 && !customersLoading && (
        <div className="empty-state">
          <Building2 size={48} style={{ color: 'var(--color-text-muted)', marginBottom: '16px' }} />
          <p>{t('admin.customers.none', 'Noch keine Kunden angelegt.')}</p>
        </div>
      )}

      {customers.length > 0 && visibleCustomers.length === 0 && (
        <p className="section-description" style={{ marginTop: 8 }}>{t('ui.noResults', 'Keine Treffer')}</p>
      )}

      {/* #105: Ansprechpartner je Kunde */}
      <Modal
        open={!!contactsCustomer}
        onClose={() => { if (!savingContact) setContactsCustomer(null); }}
        title={`${t('admin.contacts.title', 'Ansprechpartner')} — ${contactsCustomer?.name || ''}`}
        maxWidth="720px"
        closeLabel={t('common.close', 'Schließen')}
      >
        {contactError && <div className="alert alert-error">{contactError}</div>}

        <form ref={contactFormRef} onSubmit={handleSaveContact} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, alignItems: 'end', marginBottom: 16, background: 'var(--color-bg)', padding: 12, borderRadius: 6 }}>
          <div className="form-group">
            <label>{t('admin.contacts.name', 'Name')} *</label>
            <input ref={contactNameInputRef} type="text" className="form-input" value={contactForm.name}
              onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
              disabled={savingContact} required />
          </div>
          <div className="form-group">
            <label>{t('admin.contacts.phoneMobile', 'Mobil')}</label>
            <input type="text" className="form-input" value={contactForm.phoneMobile}
              onChange={(e) => setContactForm(prev => ({ ...prev, phoneMobile: e.target.value }))} disabled={savingContact} />
          </div>
          <div className="form-group">
            <label>{t('admin.contacts.phoneLandline', 'Festnetz')}</label>
            <input type="text" className="form-input" value={contactForm.phoneLandline}
              onChange={(e) => setContactForm(prev => ({ ...prev, phoneLandline: e.target.value }))} disabled={savingContact} />
          </div>
          <div className="form-group">
            <label>{t('admin.contacts.email', 'E-Mail')}</label>
            <input type="text" className="form-input" value={contactForm.email}
              onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} disabled={savingContact} />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('admin.contacts.notes', 'Notizen')}</label>
            <textarea className="form-textarea" rows={2} value={contactForm.notes}
              onChange={(e) => setContactForm(prev => ({ ...prev, notes: e.target.value }))} disabled={savingContact} />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={contactForm.isActive}
              onChange={(e) => setContactForm(prev => ({ ...prev, isActive: e.target.checked }))} disabled={savingContact} />
            {t('admin.contacts.active', 'Aktiv')}
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={savingContact || !contactForm.name.trim()}>
              {editingContactId ? <Save size={16} /> : <Plus size={16} />}
              {editingContactId
                ? t('admin.contacts.save', 'Speichern')
                : t('admin.contacts.add', 'Anlegen')}
            </button>
            {editingContactId && (
              <button type="button" className="btn btn-secondary" onClick={resetContactForm} disabled={savingContact}>
                {t('admin.contacts.cancelEdit', 'Abbrechen')}
              </button>
            )}
          </div>
        </form>

        {(contactsCustomer?.contacts || []).length === 0 ? (
          <p className="section-description">{t('admin.contacts.none', 'Noch keine Ansprechpartner hinterlegt.')}</p>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('admin.contacts.name', 'Name')}</th>
                  <th>{t('admin.contacts.phoneMobile', 'Mobil')}</th>
                  <th>{t('admin.contacts.phoneLandline', 'Festnetz')}</th>
                  <th>{t('admin.contacts.email', 'E-Mail')}</th>
                  <th>{t('admin.customers.activeCol', 'Aktiv')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(contactsCustomer?.contacts || []).map(k => (
                  <tr key={k.id} style={k.isActive === false ? { opacity: 0.55 } : undefined}>
                    <td>{k.name}</td>
                    <td>{k.phoneMobile || '—'}</td>
                    <td>{k.phoneLandline || '—'}</td>
                    <td>{k.email || '—'}</td>
                    <td>{k.isActive !== false ? '✓' : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => startEditContact(k)} disabled={contactRowBusyId === k.id}>
                          <Edit size={14} />
                          {t('admin.customers.edit', 'Bearbeiten')}
                        </button>
                        {/* #204: sichtbare Zeilen-Aktionen wie beim Kunden */}
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleToggleContactActive(k)}
                          disabled={contactRowBusyId === k.id}
                          title={k.isActive !== false
                            ? t('admin.customers.archiveAction', 'Archivieren')
                            : t('admin.customers.reactivateAction', 'Reaktivieren')}
                        >
                          {k.isActive !== false ? <Archive size={14} /> : <ArchiveRestore size={14} />}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDeleteContact(k)}
                          disabled={contactRowBusyId === k.id}
                          title={t('common.delete', 'Löschen')} aria-label={t('common.delete', 'Löschen')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomerAdmin;
